import { z } from 'zod'
import type { ProductCategoryId } from './constants'
import type { Language } from './schemas/simulation'
import type { Axis } from './taxonomy'

/**
 * 클릭식 맛 고르기 (2026-10-30 대표 결정 1). 카테고리별 맛 축 표의 단일 원본.
 * 생성 입력(describe), 서버 검사·프롬프트, 결과 화면의 맛 카드, AI 수정이 모두 이 표와 아래 TasteProfile 하나를 쓴다.
 *
 * 축 종류:
 * - level: 1~5 단계 막대. 기본 축은 모두 이 종류다.
 * - choice: 몇 개 중 하나 (제형처럼 정도가 아닌 것). 세부 축에만 있다.
 * - target: 수치 목표 (염도 %, 당도 Brix …). 카테고리마다 보통 범위가 다르다.
 *
 * 플랜 차등 (quota.tasteDetail · quota.tasteTargets, plans.ts):
 * - 기본 축: 모두 5단계.
 * - 세부 축: preview = 흐린 미리보기만, basic = basic 표시가 붙은 2~3축만 3단계(약·중·강 = 1·3·5), full = 전부 5단계와 choice.
 * - 수치 목표: hidden = 안 보임, view = AI 값과 보통 범위 보기만, edit = 직접 입력.
 *
 * 처음에는 코드 안 표로 낸다 (관리자 설정은 나중). 축 이름은 '맵기·짠맛·단맛'처럼 카테고리 표 안에서 '~맛/~기'로 맞춘다.
 * 건강식품 줄은 맛과 제형만 두고(효능 축 없음, health-claims.ts), 영유아 줄에는 짠맛 축과 염도 목표가 없다.
 */

type Words = Record<Language, string>

interface LevelAxis {
  kind: 'level'
  label: Words
  /** 1 쪽, 5 쪽 설명 */
  ends: { low: Words; high: Words }
}
interface ChoiceAxis {
  kind: 'choice'
  label: Words
  options: { id: string; label: Words }[]
}
interface TargetAxis {
  kind: 'target'
  label: Words
  unit: '%' | 'Brix'
  step: number
  min: number
  max: number
}
export type TasteAxisDef = LevelAxis | ChoiceAxis | TargetAxis

const w = (ko: string, en: string, ja: string, zh: string): Words => ({ ko, en, ja, zh })
const level = (label: Words, low: Words, high: Words): LevelAxis => ({
  kind: 'level',
  label,
  ends: { low, high },
})
const target = (label: Words, unit: TargetAxis['unit'], step: number, max: number): TargetAxis => ({
  kind: 'target',
  label,
  unit,
  step,
  min: 0,
  max,
})

/** 축 사전. 같은 뜻이면 카테고리끼리 같은 id 를 쓴다 */
export const TASTE_AXES = {
  spicy: level(
    w('맵기', 'Heat', '辛さ', '辣度'),
    w('안 맵게', 'Mild', '辛くない', '不辣'),
    w('아주 맵게', 'Very hot', '激辛', '特辣'),
  ),
  salty: level(
    w('짠맛', 'Saltiness', '塩味', '咸度'),
    w('싱겁게', 'Light', '薄め', '清淡'),
    w('짜게', 'Salty', 'しょっぱめ', '偏咸'),
  ),
  sweet: level(
    w('단맛', 'Sweetness', '甘さ', '甜度'),
    w('안 달게', 'Not sweet', '甘くない', '不甜'),
    w('아주 달게', 'Very sweet', 'とても甘い', '很甜'),
  ),
  sour: level(
    w('신맛', 'Sourness', '酸味', '酸度'),
    w('거의 없게', 'Barely', '控えめ', '微酸'),
    w('새콤하게', 'Tangy', '酸っぱめ', '很酸'),
  ),
  umami: level(
    w('감칠맛', 'Umami', '旨味', '鲜味'),
    w('담백하게', 'Clean', 'あっさり', '清淡'),
    w('진하게', 'Deep', '濃いめ', '浓鲜'),
  ),
  bitter: level(
    w('쓴맛', 'Bitterness', '苦味', '苦味'),
    w('거의 없게', 'Barely', '控えめ', '微苦'),
    w('쌉쌀하게', 'Bold', 'しっかり', '浓苦'),
  ),
  astringent: level(
    w('떫은맛', 'Astringency', '渋み', '涩感'),
    w('부드럽게', 'Smooth', 'まろやか', '柔和'),
    w('떫게', 'Astringent', '渋め', '偏涩'),
  ),
  richness: level(
    w('진하기', 'Richness', '濃さ', '浓郁度'),
    w('맑게', 'Light', 'あっさり', '清爽'),
    w('진하게', 'Rich', '濃厚', '浓郁'),
  ),
  nutty: level(
    w('고소함', 'Nuttiness', '香ばしさ', '坚果香'),
    w('은은하게', 'Subtle', 'ほのか', '淡'),
    w('아주 고소하게', 'Very nutty', 'とても香ばしい', '很香'),
  ),
  smoky: level(
    w('훈연', 'Smokiness', '燻製香', '烟熏味'),
    w('없게', 'None', 'なし', '无'),
    w('강하게', 'Strong', '強め', '浓'),
  ),
  buttery: level(
    w('버터 풍미', 'Butteriness', 'バター風味', '黄油香'),
    w('담백하게', 'Light', '控えめ', '清淡'),
    w('버터 가득', 'Rich butter', 'たっぷり', '浓郁'),
  ),
  roast: level(
    w('볶음 정도', 'Roast', '焙煎度', '烘焙度'),
    w('약하게', 'Light', '浅め', '浅'),
    w('강하게', 'Dark', '深め', '深'),
  ),
  aging: level(
    w('숙성도', 'Aging', '熟成度', '熟成度'),
    w('덜 익게', 'Fresh', '浅め', '新鲜'),
    w('푹 익게', 'Well aged', 'よく熟成', '久熟'),
  ),
  aroma: level(
    w('향 세기', 'Aroma', '香り', '香气'),
    w('은은하게', 'Subtle', 'ほのか', '淡'),
    w('진하게', 'Strong', '強め', '浓'),
  ),
  viscosity: level(
    w('점도', 'Consistency', 'とろみ', '稠度'),
    w('묽게', 'Thin', 'さらさら', '稀'),
    w('걸쭉하게', 'Thick', 'どろっと', '稠'),
  ),
  crunch: level(
    w('바삭함', 'Crunch', 'サクサク感', '酥脆度'),
    w('부드럽게', 'Soft', 'やわらか', '软'),
    w('아주 바삭하게', 'Very crunchy', 'ザクザク', '很脆'),
  ),
  crisp: level(
    w('아삭함', 'Crispness', 'シャキシャキ感', '爽脆度'),
    w('무르게', 'Soft', 'やわらか', '软'),
    w('아삭하게', 'Crisp', 'シャキシャキ', '脆'),
  ),
  chewy: level(
    w('쫄깃함', 'Chewiness', '弾力', '嚼劲'),
    w('부드럽게', 'Tender', 'やわらか', '软'),
    w('쫄깃하게', 'Chewy', 'もちもち', '有嚼劲'),
  ),
  moist: level(
    w('촉촉함', 'Moistness', 'しっとり感', '湿润度'),
    w('포슬포슬', 'Crumbly', 'ほろほろ', '松散'),
    w('촉촉하게', 'Moist', 'しっとり', '湿润'),
  ),
  carbonation: level(
    w('탄산', 'Fizz', '炭酸', '气泡'),
    w('없게', 'Still', 'なし', '无'),
    w('강하게', 'Very fizzy', '強炭酸', '强'),
  ),
  body: level(
    w('바디감', 'Body', 'コク', '醇厚度'),
    w('가볍게', 'Light', '軽め', '轻盈'),
    w('묵직하게', 'Full', '重め', '醇厚'),
  ),
  particle: level(
    w('입자감', 'Texture', '粒感', '颗粒感'),
    w('곱게', 'Smooth', 'なめらか', '细腻'),
    w('굵게', 'Chunky', '粗め', '粗'),
  ),
  thickness: level(
    w('두께', 'Thickness', '厚さ', '厚度'),
    w('얇게', 'Thin', '薄め', '薄'),
    w('두껍게', 'Thick', '厚め', '厚'),
  ),
  portion: level(
    w('양', 'Portion', '量', '份量'),
    w('적게', 'Small', '少なめ', '少'),
    w('넉넉하게', 'Generous', 'たっぷり', '多'),
  ),
  form: {
    kind: 'choice',
    label: w('제형', 'Format', '形状', '剂型'),
    options: [
      { id: 'tablet', label: w('정제', 'Tablet', '錠剤', '片剂') },
      { id: 'capsule', label: w('캡슐', 'Capsule', 'カプセル', '胶囊') },
      { id: 'powder', label: w('분말', 'Powder', '粉末', '粉剂') },
      { id: 'jelly', label: w('젤리', 'Jelly', 'ゼリー', '果冻') },
      { id: 'liquid', label: w('액상', 'Liquid', '液体', '液体') },
    ],
  },
  salt_pct: target(w('염도', 'Salt', '塩分', '盐度'), '%', 0.1, 25),
  sugar_brix: target(w('당도', 'Sugar', '糖度', '糖度'), 'Brix', 1, 80),
  sugar_pct: target(w('설탕', 'Sugar', '砂糖', '砂糖'), '%', 1, 60),
  fat_pct: target(w('유지방', 'Milk fat', '乳脂肪', '乳脂'), '%', 0.5, 60),
  abv_pct: target(w('도수', 'ABV', '度数', '酒精度'), '%', 0.5, 60),
} as const satisfies Record<string, TasteAxisDef>
export type TasteAxisId = keyof typeof TASTE_AXES

type AxisOf<K extends TasteAxisDef['kind']> = {
  [Id in TasteAxisId]: (typeof TASTE_AXES)[Id]['kind'] extends K ? Id : never
}[TasteAxisId]

/** 카테고리 한 줄. 순서가 화면 순서다 */
export interface TasteAxisSet {
  /** 기본 축 3~4개, 모두 level. 무료부터 5단계 */
  base: AxisOf<'level'>[]
  /** 세부 축. basic: true 인 것만 Basic 플랜이 3단계로 고른다 (level 만) */
  detail: { axis: Exclude<TasteAxisId, AxisOf<'target'>>; basic?: true }[]
  /** 수치 목표와 그 카테고리의 보통 범위 */
  targets: { axis: AxisOf<'target'>; normal: readonly [number, number] }[]
}

const set = (
  base: TasteAxisSet['base'],
  detail: TasteAxisSet['detail'],
  targets: TasteAxisSet['targets'] = [],
): TasteAxisSet => ({ base, detail, targets })
const b = (axis: AxisOf<'level'>) => ({ axis, basic: true }) as const
const salt = (lo: number, hi: number) => ({ axis: 'salt_pct', normal: [lo, hi] }) as const
const brix = (lo: number, hi: number) => ({ axis: 'sugar_brix', normal: [lo, hi] }) as const

/** 파이프라인 카테고리 18개 (맛 칩과 같은 키) */
export const TASTE_AXIS_SETS: Record<ProductCategoryId, TasteAxisSet> = {
  sauce: set(
    ['spicy', 'salty', 'sweet', 'viscosity'],
    [b('sour'), b('umami'), { axis: 'aroma' }, { axis: 'particle' }],
    [salt(1.5, 3), brix(8, 16)],
  ),
  beverage: set(['sweet', 'sour', 'carbonation', 'body'], [b('bitter'), b('aroma')], [brix(8, 12)]),
  juice: set(['sweet', 'sour', 'particle'], [b('aroma'), b('body')], [brix(10, 14)]),
  alcohol: set(
    ['sweet', 'sour', 'body', 'carbonation'],
    [b('bitter'), b('aroma')],
    [{ axis: 'abv_pct', normal: [4, 17] }],
  ),
  snacks: set(
    ['salty', 'sweet', 'crunch', 'thickness'],
    [b('spicy'), b('umami'), { axis: 'aroma' }],
    [salt(1, 2.5)],
  ),
  bakery: set(
    ['sweet', 'moist', 'buttery', 'chewy'],
    [b('salty'), b('aroma')],
    [{ axis: 'sugar_pct', normal: [5, 20] }],
  ),
  dessert: set(
    ['sweet', 'viscosity', 'chewy'],
    [b('sour'), b('aroma')],
    [brix(15, 30), { axis: 'fat_pct', normal: [3, 15] }],
  ),
  fermented: set(
    ['spicy', 'sour', 'aging', 'crisp'],
    [b('salty'), b('sweet'), b('umami'), { axis: 'aroma' }],
    [salt(2, 3.5)],
  ),
  noodles: set(
    ['richness', 'spicy', 'thickness', 'portion'],
    [b('salty'), b('umami'), b('chewy'), { axis: 'aroma' }],
    [salt(0.8, 1.5)],
  ),
  meal: set(
    ['spicy', 'salty', 'richness', 'portion'],
    [b('sweet'), b('umami'), { axis: 'aroma' }],
    [salt(0.6, 1.2)],
  ),
  'soup-base': set(['richness', 'salty', 'umami'], [b('aroma'), b('spicy')], [salt(0.8, 2)]),
  'processed-meat': set(
    ['salty', 'smoky', 'chewy'],
    [b('spicy'), b('sweet'), { axis: 'aroma' }],
    [salt(1.5, 3)],
  ),
  'processed-seafood': set(
    ['salty', 'umami', 'chewy'],
    [b('spicy'), b('sweet'), { axis: 'aroma' }],
    [salt(1.5, 3)],
  ),
  dairy: set(
    ['sweet', 'sour', 'viscosity'],
    [b('salty'), b('aroma')],
    [{ axis: 'fat_pct', normal: [1, 10] }],
  ),
  'grain-legume-nut': set(
    ['nutty', 'sweet', 'crunch'],
    [b('salty'), b('roast'), { axis: 'particle' }],
    [salt(0.3, 1.5)],
  ),
  'fruit-vegetable': set(['sweet', 'sour', 'crisp'], [b('salty'), b('aroma')], [brix(10, 20)]),
  'fats-oils': set(['nutty', 'roast', 'spicy'], [b('aroma'), b('smoky')]),
  other: set(
    ['spicy', 'salty', 'sweet', 'richness'],
    [b('sour'), b('umami'), { axis: 'aroma' }],
    [salt(0.8, 2), brix(5, 15)],
  ),
}

/**
 * 식품 축 대분류 줄 (taxonomy.ts 의 majorId). 커피·차·떡·건강식품·영유아는 categoryId 가 beverage·dessert·other 로
 * 묶여 있어 따로 둔다. 있으면 카테고리 줄보다 먼저 쓴다
 */
export const TASTE_AXIS_MAJOR_SETS: Partial<Record<string, TasteAxisSet>> = {
  coffee: set(['bitter', 'sour', 'body', 'sweet'], [b('roast'), b('aroma')], [brix(0, 8)]),
  tea: set(['astringent', 'sweet', 'aroma'], [b('roast'), b('body')], [brix(0, 8)]),
  tteok: set(
    ['sweet', 'chewy', 'nutty'],
    [b('salty'), b('aroma')],
    [{ axis: 'sugar_pct', normal: [5, 15] }],
  ),
  // 맛과 제형만. 효능 축을 두지 않는다 (로드맵 P8)
  supplement: set(['sweet', 'bitter', 'sour'], [b('aroma'), { axis: 'form' }]),
  // 짠맛 축과 염도 목표를 두지 않는다
  baby: set(['sweet', 'particle', 'viscosity'], [b('aroma')]),
}

/** 이 제품의 축 줄. 식품 축 대분류 줄이 있으면 그것, 없으면 파이프라인 카테고리 줄 */
export function tasteAxisSet(
  categoryId: ProductCategoryId,
  majorId?: string | null,
  axis: Axis = 'product',
): TasteAxisSet {
  const major = axis === 'product' && majorId ? TASTE_AXIS_MAJOR_SETS[majorId] : undefined
  return major ?? TASTE_AXIS_SETS[categoryId] ?? TASTE_AXIS_SETS.other
}

// ── 맛 값 ───────────────────────────────────────────────────────

/**
 * 맛 값. 고른 축만 담는다(빈 축 = AI 가 정함). 생성 입력·결과·AI 수정이 같은 형태를 쓴다.
 * level 은 1~5 정수, target 은 숫자, choice 는 option id
 */
export const tasteProfileSchema = z.object({
  v: z.literal(1),
  values: z.record(z.string().max(30), z.union([z.number(), z.string().max(30)])),
})
export type TasteProfile = z.infer<typeof tasteProfileSchema>

/** 세부 축 권한 (quota.tasteDetail) */
export const TASTE_DETAIL_ACCESS = ['preview', 'basic', 'full'] as const
export type TasteDetailAccess = (typeof TASTE_DETAIL_ACCESS)[number]
/** 수치 목표 권한 (quota.tasteTargets) */
export const TASTE_TARGET_ACCESS = ['hidden', 'view', 'edit'] as const
export type TasteTargetAccess = (typeof TASTE_TARGET_ACCESS)[number]
export interface TasteAccess {
  tasteDetail: TasteDetailAccess
  tasteTargets: TasteTargetAccess
}

/** Basic 의 3단계(약·중·강)가 5단계 값으로 놓이는 자리 */
export const BASIC_TASTE_LEVELS = [1, 3, 5] as const

const isLevel = (v: unknown): v is number => Number.isInteger(v) && Number(v) >= 1 && Number(v) <= 5

/** 이 플랜이 이 축을 직접 고를 수 있는지와 고를 수 있는 값. null = 못 고름 */
export function tasteAxisAccess(
  axisSet: TasteAxisSet,
  axis: string,
  access: TasteAccess,
): 'level' | 'basic-level' | 'choice' | 'target' | null {
  if ((axisSet.base as string[]).includes(axis)) return 'level'
  const detail = axisSet.detail.find((d) => d.axis === axis)
  if (detail) {
    const def = TASTE_AXES[detail.axis]
    if (access.tasteDetail === 'full') return def.kind === 'choice' ? 'choice' : 'level'
    if (access.tasteDetail === 'basic' && detail.basic && def.kind === 'level') return 'basic-level'
    return null
  }
  if (axisSet.targets.some((t) => t.axis === axis))
    return access.tasteTargets === 'edit' ? 'target' : null
  return null
}

function allowedValue(
  axis: string,
  kind: NonNullable<ReturnType<typeof tasteAxisAccess>>,
  value: unknown,
): number | string | null {
  if (kind === 'level') return isLevel(value) ? value : null
  if (kind === 'basic-level')
    return (BASIC_TASTE_LEVELS as readonly unknown[]).includes(value) ? (value as number) : null
  const def = TASTE_AXES[axis as TasteAxisId]
  if (kind === 'choice' && def.kind === 'choice')
    return def.options.some((o) => o.id === value) ? (value as string) : null
  if (kind === 'target' && def.kind === 'target' && typeof value === 'number') {
    if (!Number.isFinite(value) || value < def.min || value > def.max) return null
    // 단계 눈금에 맞춘다 (0.1% 단위 등). 부동소수 꼬리는 소수 둘째 자리에서 자른다
    return Number((Math.round(value / def.step) * def.step).toFixed(2))
  }
  return null
}

/**
 * 사용자가 보낸 맛 값을 이 카테고리와 플랜에 맞게 거른다 (서버의 이중 잠금, 앱의 화면 상태가 같은 함수를 쓴다).
 * 표에 없는 축, 범위 밖 값, 플랜 밖 축(세부·수치)은 버린다. Basic 의 세부 축은 1·3·5 만 받는다
 */
export function sanitizeTasteProfile(
  profile: TasteProfile | null | undefined,
  axisSet: TasteAxisSet,
  access: TasteAccess,
): TasteProfile {
  const values: TasteProfile['values'] = {}
  for (const [axis, value] of Object.entries(profile?.values ?? {})) {
    const kind = tasteAxisAccess(axisSet, axis, access)
    const allowed = kind ? allowedValue(axis, kind, value) : null
    if (allowed !== null) values[axis] = allowed
  }
  return { v: 1, values }
}

/** 결과에 남길 AI 값 (생성·수정 출력). 표에 있는 축만, level 은 1~5 정수로 반올림해 자르고 수치는 범위 안만 */
export function normalizeResultTasteProfile(
  raw: unknown,
  axisSet: TasteAxisSet,
): TasteProfile | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const known = new Set<string>([
    ...axisSet.base,
    ...axisSet.detail.map((d) => d.axis),
    ...axisSet.targets.map((t) => t.axis),
  ])
  const values: TasteProfile['values'] = {}
  for (const [axis, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!known.has(axis)) continue
    const def = TASTE_AXES[axis as TasteAxisId]
    const n = typeof value === 'string' ? Number.parseFloat(value) : value
    if (def.kind === 'level' && typeof n === 'number' && Number.isFinite(n))
      values[axis] = Math.min(5, Math.max(1, Math.round(n)))
    else if (def.kind === 'target' && typeof n === 'number' && n >= def.min && n <= def.max)
      values[axis] = Number(n.toFixed(2))
    else if (def.kind === 'choice' && def.options.some((o) => o.id === value))
      values[axis] = value as string
  }
  return Object.keys(values).length > 0 ? { v: 1, values } : undefined
}

/**
 * AI 수정 뒤 결과의 맛 값. 기본 축과 수치 목표는 수정본 값을 받고, 세부 축은 이 플랜이 직접 고를 수 있을 때만 받는다 —
 * 무료 사용자가 채팅으로 세부 축을 말하면 배합 글에는 반영되지만 막대는 그대로다 (대표 결정 1).
 * 수정본에 칸이 없거나 깨졌으면 원래 값을 둔다. raw 는 { v, values } 나 납작한 { 축: 값 } 둘 다 받는다
 */
export function mergeEditedTasteProfile(
  before: TasteProfile | undefined,
  raw: unknown,
  axisSet: TasteAxisSet,
  access: TasteAccess,
): TasteProfile | undefined {
  const flat =
    raw && typeof raw === 'object' && 'values' in raw ? (raw as { values: unknown }).values : raw
  const edited = normalizeResultTasteProfile(flat, axisSet)
  if (!edited) return before
  const values: TasteProfile['values'] = { ...before?.values }
  for (const [axis, value] of Object.entries(edited.values)) {
    const isDetail = axisSet.detail.some((d) => d.axis === axis)
    if (!isDetail || tasteAxisAccess(axisSet, axis, access) !== null) values[axis] = value
  }
  return { v: 1, values }
}
